import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {repoAppBuilder} from '@github-ui/repo-app-builder'
import type {ShouldRevalidateFunction} from '@github-ui/react-router'
import type {Workflow} from '../views/models'
import {type WorkflowRouteParams, workflowRouteParam} from '../workflow-path'

export interface ActionsSurveyPayload {
  surveyLink: string
  surveyOpenCallbackPath: string
  surveyDismissCallbackPath: string
}

/** Payload for the stateless GitHub App installation-token format notice. */
export interface StatelessS2sTokenFormatChangeBannerPayload {
  /** Path that stores the current user's 90-day notice dismissal. */
  dismissPath: string
}

/** Payload for the repository Actions rate-limit warning banner. */
export interface RepositoryRateLimitBannerPayload {
  /** Customer-facing banner title, rendered as-is. */
  title: string
  /** Customer-facing banner message, rendered as-is. */
  message: string
  /** URL for an inline "Learn more" link. */
  documentationUrl: string
}

/**
 * Payload for the banner shown on a single workflow's runs page when that
 * workflow is disabled (fork/inactivity/manual/admin) or about to be
 * auto-disabled for inactivity ("churning").
 */
export interface DisabledWorkflowBannerPayload {
  variant: 'disabled_fork' | 'disabled_inactivity' | 'disabled_manually' | 'disabled_admin' | 'churning'
  /** ID of the workflow whose cached sidebar state should be updated after enabling. */
  workflowId: number
  /** Whether the viewer can enable the workflow. */
  canEnable: boolean
  /** Path to POST to in order to enable the workflow. */
  enablePath: string
  /** Number of inactivity days after which a scheduled workflow is auto-disabled. */
  repositoryInactivityThresholdDays: number
  /** Support URL linked from the `disabled_admin` variant. */
  supportUrl: string
}

/**
 * Payload for the Actions billing-warning banner
 */
export interface BillingWarningBannerPayload {
  /** Which body copy branch to render; `other` renders the heading with no body (existing Rails gap). */
  variant: 'legacy_plan' | 'access_not_included' | 'disabled' | 'other'
  /** The billing owner's display login, bolded in the `access_not_included` copy. */
  ownerLogin: string
  /** Path to the org billing/plans settings page for the "Upgrade" button; present only when the viewer may see it. */
  upgradePath?: string | null
  /** URL for the "Learn more about GitHub Actions" link; present only when the org-upsell row should render. */
  learnMoreUrl?: string | null
}

/**
 * Payload for the billing-critical (trust-tier usage limit) banner
 */
export interface BillingCriticalBannerPayload {
  /** Whether the viewer is an admin of the billable owner; selects the admin vs. non-admin copy. */
  isAdmin: boolean
}

export interface WorkflowsLayoutPayload {
  workflows: Workflow[]
  totalCount: number
  pinnedWorkflowIds: number[]
  allowPinning: boolean
  canCreateWorkflow: boolean
  showOnlyRequiredWorkflows?: boolean
  showDeployments: boolean
  showAttestations: boolean
  showRunners: boolean
  lockfilePath?: string | null
  showUsageMetrics: boolean
  isFork: boolean
  workflowsPath?: string | null
  showActionsNotEnabledBanner: boolean
  banners: {
    enablePath: string
    actionsSurvey?: ActionsSurveyPayload
    statelessS2sTokenFormatChangeBanner?: StatelessS2sTokenFormatChangeBannerPayload
    /** Present only on the selected-workflow route when that workflow is disabled or churning. */
    disabledWorkflowBanner?: DisabledWorkflowBannerPayload
    /** Absent or `null` when the Launch RPC lookup fails or no rate limits are active; present on success with an active rate limit. */
    repositoryRateLimitBanner?: RepositoryRateLimitBannerPayload | null
    /** Present when the repository owner's `Billing::ActionsPermission` check fails (legacy plan, billing lock, or another not-allowed reason). */
    billingWarningBanner?: BillingWarningBannerPayload | null
    /** Present when the repository's billable owner has hit its trust-tier usage limit. */
    billingCriticalBanner?: BillingCriticalBannerPayload | null
  }
  // Server-read seed of the shared sidebar cookie, used to keep SSR and hydration in sync.
  sidebarExpanded?: boolean | null
}

/**
 * Revalidates the workflows-layout loader on workflow page change, or when the `lab`
 * search param changes (the query above depends on it too), so the sidebar and banners
 * don't stay stale for the previous workflow/lab context.
 */
export const shouldRevalidateWorkflowsLayout: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  currentUrl,
  nextUrl,
}) => {
  return (
    currentParams.owner !== nextParams.owner ||
    currentParams.repo !== nextParams.repo ||
    workflowRouteParam(currentParams) !== workflowRouteParam(nextParams) ||
    currentUrl.searchParams.get('lab') !== nextUrl.searchParams.get('lab') ||
    isWorkflowSurface(currentUrl.pathname) !== isWorkflowSurface(nextUrl.pathname)
  )
}

function isWorkflowSurface(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, '')
  return !normalizedPathname.endsWith('/actions/caches') && !normalizedPathname.endsWith('/actions/runners')
}

/**
 * Builds the query dependencies for the workflows-layout `_layout` fetch: the selected
 * workflow's route param and the `lab` search param, so the request re-fetches whenever
 * either changes.
 */
export function workflowsLayoutQueryDeps({
  pathname,
  params,
  searchParams,
}: {
  pathname: string
  params: WorkflowRouteParams
  searchParams: URLSearchParams
}) {
  return {
    pathname: `/${encodeURIComponent(params.owner ?? '')}/${encodeURIComponent(params.repo ?? '')}/actions/_layout`,
    searchParams: {
      workflow_file_name: workflowRouteParam(params) || null,
      lab: searchParams.get('lab') === 'true' ? 'true' : null,
      workflow_runs_view: isWorkflowSurface(pathname) ? 'true' : null,
    },
  }
}

export function buildWorkflowsLayoutRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('workflowsLayoutRoute', {
    path: '/:owner/:repo/actions',
    queries: [
      mainQuery<WorkflowsLayoutPayload>({
        queryDeps: workflowsLayoutQueryDeps,
        staleTimeForNavigation: 360000,
      }),
    ],
    shouldRevalidate: shouldRevalidateWorkflowsLayout,
  })
}

export const workflowsLayoutRoute = buildWorkflowsLayoutRoute(repoAppBuilder)
